/**
 * Unwrap every balanced underline pair, leaving the contents in place — the
 * pair-only, attribute-tolerant strip shared by the plain-copy annotation
 * removal (`removeAnnotationFromMarkdown`) and the plain-text sink
 * (`stripVisualEmphasis`). A global lazy pair regex re-scans from every
 * opener when a closer never comes (`<u a>...<u b>...` with no `</u>`), and
 * the attribute-tolerant opener pattern itself retries from every position
 * on a long unterminated `<u ...` run — both quadratic on pasted or cut-down
 * input. This walk instead touches each byte at most a constant number of
 * times:
 *
 * - find each `<u`/`<U` hit with a literal scan, then verify the tag the only
 *   way an opener can exist: a `>` after a bare name or a well-formed,
 *   quote-aware attribute run with no self-closing `/` (the pair source's
 *   exact grammar, checked by hand).
 * - an unterminated candidate (no `>` downstream, or an unclosed quote) means
 *   nothing downstream can be an opener — every opener needs a `>` — so stop
 *   and copy the rest verbatim.
 * - each pair is its opening tag to the FIRST closer that follows it; an
 *   opener with no closer keeps the rest of the input untouched.
 *
 * That reproduces the pinned nested behavior — the inner pair's closer is the
 * outer opener's first closer, so the inner opener alone survives — and never
 * consumes a self-closing `<u/>` (not an opener per the pair grammar).
 */
export const stripUnderlineHtmlPairs = (value: string): string => {
  const findCandidate = /<u/gi;
  const findCloser = /<\/u\s*>/gi;
  let out = '';
  let position = 0;
  let open: RegExpExecArray | null;
  while ((open = findCandidate.exec(value)) !== null) {
    // Verify the candidate is an opener; `tagEnd` is the index just past `>`.
    let index = open.index + 2;
    let tagEnd = -1;
    let quote: string | null = null;
    const firstChar = index < value.length ? value[index] : undefined;
    if (firstChar === '>') {
      tagEnd = index + 1;
    } else if (firstChar !== undefined && /\s/.test(firstChar)) {
      while (index < value.length) {
        const char = value[index] as string;
        if (quote !== null) {
          if (char === quote) quote = null;
        } else if (char === '"' || char === "'") {
          quote = char;
        } else if (char === '>') {
          tagEnd = index + 1;
        } else if (char === '/') {
          break; // self-closing — not an opener
        }
        if (tagEnd !== -1) break;
        index += 1;
      }
    }
    if (tagEnd === -1) {
      if (quote !== null || index >= value.length) {
        // Unterminated tag (unclosed quote or exhausted input): nothing
        // downstream can be an opener — every opener needs a `>`.
        break;
      }
      // Invalid candidate (`<uX` glued text, self-closing `<u/>`): scan on.
      continue;
    }
    findCloser.lastIndex = tagEnd;
    const close = findCloser.exec(value);
    if (!close) break;
    out += value.slice(position, open.index) + value.slice(tagEnd, close.index);
    position = close.index + close[0].length;
    findCandidate.lastIndex = position;
  }
  return out + value.slice(position);
};
